import React, { useEffect, useState } from 'react';

import { type AppState } from '@excalidraw/excalidraw/types/types';
import { ZoomIn, ZoomOut } from '@mui/icons-material';
import { styled, Box, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { type NormalizedZoomValue } from '../../types';

const Container = styled(Box)`
    display: flex;
    flex-flow: row;
`;

export const getNormalizedZoom = (zoom: number): NormalizedZoomValue =>
    Math.max(0.1, Math.min(zoom, 30)) as NormalizedZoomValue;

export const getStateForZoom = (
    {
        viewportX,
        viewportY,
        nextZoom
    }: {
        viewportX: number;
        viewportY: number;
        nextZoom: NormalizedZoomValue;
    },
    appState: AppState
) => {
    const appLayerX = viewportX - appState.offsetLeft;
    const appLayerY = viewportY - appState.offsetTop;

    const currentZoom = appState.zoom.value;

    // get original scroll position without zoom
    const baseScrollX = appState.scrollX + (appLayerX - appLayerX / currentZoom);
    const baseScrollY = appState.scrollY + (appLayerY - appLayerY / currentZoom);

    // get scroll offsets for target zoom level
    const zoomOffsetScrollX = -(appLayerX - appLayerX / nextZoom);
    const zoomOffsetScrollY = -(appLayerY - appLayerY / nextZoom);

    return {
        scrollX: baseScrollX + zoomOffsetScrollX,
        scrollY: baseScrollY + zoomOffsetScrollY,
        zoom: {
            value: nextZoom
        }
    };
};

type Props = {
    excalidrawZoom: number;
    onZoomChange: (newZoom: number) => void;
};

const ZOOM_STEP = 0.1;

const ZoomTools = React.memo((props: Props) => {
    const { t } = useTranslation(['whiteboard', 'common']);

    const [naturalZoomValue, setNaturalZoomValue] = useState<number | ''>(props.excalidrawZoom * 100);

    useEffect(() => {
        setNaturalZoomValue(parseFloat((props.excalidrawZoom * 100).toFixed(0)));
    }, [props.excalidrawZoom]);

    const setExcalidrawZoom = (value: number) => {
        setNaturalZoomValue(Math.round(value * 100));
        props.onZoomChange(value);
    };

    const handleZoomButtonClick = (step: number) => {
        setExcalidrawZoom(parseFloat(getNormalizedZoom(props.excalidrawZoom + step).toFixed(1)));
    };

    return (
        <Container>
            <Tooltip title={t('whiteboard:buttons.zoomIn')} placement="right">
                <IconButton
                    aria-label={t('whiteboard:buttons.zoomIn')}
                    onClick={() => handleZoomButtonClick(ZOOM_STEP)}
                    disabled={Number(naturalZoomValue) >= 1000}
                >
                    <ZoomIn />
                </IconButton>
            </Tooltip>
            <Tooltip title={t('whiteboard:buttons.zoomOut')} placement="right">
                <IconButton
                    aria-label={t('whiteboard:buttons.zoomOut')}
                    onClick={() => handleZoomButtonClick(-ZOOM_STEP)}
                    disabled={Number(naturalZoomValue) <= 10}
                >
                    <ZoomOut />
                </IconButton>
            </Tooltip>
        </Container>
    );
});

export default ZoomTools;
